import { useState } from 'react';
import { deserialize, serialize } from 'serializr';
import { generatePath } from 'react-router-dom';
import axiosInstance from 'interceptor/axiosInstance';
import { ApiRoutes } from 'routes/routeConstants/apiRoutes';
import { ScopeData } from 'models/ActivityDataEntryScopes/scopes.model';
import { Scope1DesiredData } from 'models/ActivityDataScope1DesiredData/scope1DesiredData.model';
import { Scope2DesiredData } from 'models/ActivityDataScope2DesiredData/scope2DesiredData.model';
import { LocationData } from 'models/ActivityDataEntryScopes/locationData.model';
import { OrganizationMetaData } from 'models/OrganizationMetaData.ts';
import { ScopeThreeData } from 'models/ActivityDataEntryScopes/scopeThree.model';
import { BusinessTravelData } from 'models/ActivityDataEntryScopes/businessTravel.model';
import { ProductionData } from 'models/ProductionData/productionData.model'; // Frontend-only
import { NotificationTypes } from 'enums/notificationTypes';
import { ScopeCalculatorPropsEnums } from 'enums/ScopeCalculatorEnums';
import Notification from 'shared/components/Notification';
import { sortItemsById } from 'shared/utils/sortedArrayById';
import { handleBlobDownload } from 'shared/utils/handleBlobDownload';
import { getMonthName } from 'shared/utils/monthName';

import {
  category5, category6, dataUpdatedMessage, locationBased, marketBased, mobileSources,
  processSources, scope3, stationaryCombustion, wasteTypesKey,
} from 'constants/constants';
import { RcFile } from 'antd/lib/upload';
import { UploadModalConstants } from 'constants/uploadModal.constants';
import { BLOB_TYPE, JSON_RESPONSE } from 'views/CompanyDashboard/constants';
import { useParentOrganizationContext } from 'context/ParentOrganisationContext';

export interface ScopeMap {
  [fuelName: string]: string | number | null;
}

const { XLSX_TYPE, CONTENT_TYPE, POST, INVALID_FILE_FORMAT, FILE } =
  UploadModalConstants;

const ActivityDataEntryService = () => {
  const { organizationData } = useParentOrganizationContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [isSuccessfulUpload, setIsSuccessfulUpload] = useState<boolean>(false);
  const [scope1Details, setScope1Details] = useState<Scope1DesiredData>({});
  const [scope2Details, setScope2Details] = useState<Scope2DesiredData>({});
  const [orgListMeta, setOrgListMeta] = useState<OrganizationMetaData[]>([]);
  const [wasteTypeListMeta, setwasteTypeListMeta] = useState<OrganizationMetaData[]>([]);
  const [errorFile, setErrorFile] = useState<Blob>(); // blob file containing validation errors from uploads.
  const [scopeThreeData, setScopeThreeData] = useState<ScopeThreeData>();
  const [businessTravelData, setBusinessTravelData] = useState<BusinessTravelData>();
  // Production Data - Retrieved from backend API, same pattern as Scope 1/2
  const [productionData, setProductionData] = useState<ProductionData>({});

  const createDesiredScopeData = (locationScopeDetails?: LocationData) => {
    const scope1Items =
      locationScopeDetails?.energyAndFuelSource?.filter(
        item => item.scopeCategory === ScopeCalculatorPropsEnums.SCOPE_1
      ) ?? [];
    const scope2Items =
      locationScopeDetails?.energyAndFuelSource?.filter(
        item => item.scopeCategory === ScopeCalculatorPropsEnums.SCOPE_2
      ) ?? [];

    const segregateScope1Data = (data: ScopeData[]): Scope1DesiredData => {
      return data.reduce<Scope1DesiredData>(
        (acc, item) => {
          switch (item.scopeFieldCategory) {
            case mobileSources:
              acc?.mobileSources?.push(item);
              break;
            case stationaryCombustion:
              acc?.stationaryCombustion?.push(item);
              break;
            case processSources:
              acc?.processSources?.push(item);
              break;
            default:
              acc?.fugitiveEmissions?.push(item);
              break;
          }
          return acc;
        },
        {
          mobileSources: [],
          stationaryCombustion: [],
          processSources: [],
          fugitiveEmissions: [],
        }
      );
    };
    const sortedScope1Data = sortItemsById(scope1Items);

    const desiredSegregatedScope1Data = segregateScope1Data(sortedScope1Data);

    const segregateScope2Data = (data: ScopeData[]): Scope2DesiredData => {
      return data.reduce<Scope2DesiredData>(
        (acc, item) => {
          if (item.scopeFieldCategory === locationBased) {
            acc?.locationBased?.push(item);
          } else if (item.scopeFieldCategory === marketBased) {
            acc?.marketBased?.push(item);
          }
          return acc;
        },
        { locationBased: [], marketBased: [] }
      );
    };
    const sortedScope2Data = sortItemsById(scope2Items);
    const desiredSegregatedScope2Data = segregateScope2Data(sortedScope2Data);

    setScope1Details(desiredSegregatedScope1Data);
    setScope2Details(desiredSegregatedScope2Data);

    // Extract revenue and revenue_unit from backend API response (scope_completion_status.revenue, scope_completion_status.revenue_unit)
    // Same pattern as scope1/scope2 - just extract directly from API response
    if (locationScopeDetails?.scopeCompletionStatus) {
      const { revenue, revenueUnit } = locationScopeDetails.scopeCompletionStatus;

      const productionDataObj = new ProductionData();

      // Ensure proper conversion from DECIMAL string to number if needed
      if (revenue === null || revenue === undefined) {
        productionDataObj.revenue = null;
      } else if (typeof revenue === 'string') {
        const parsed = parseFloat(revenue);
        productionDataObj.revenue = isNaN(parsed) ? null : parsed;
      } else {
        productionDataObj.revenue = revenue;
      }

      // Store the fetched currency unit (EUR, USD, or CAD) from backend
      // Validate and normalize the currency value - preserve what user selected and saved
      const validCurrencies = ['EUR', 'USD', 'CAD'];
      const normalizedCurrency = revenueUnit?.toString().trim().toUpperCase() || '';
      productionDataObj.revenueUnit = validCurrencies.includes(normalizedCurrency)
        ? normalizedCurrency
        : null;

      setProductionData(productionDataObj);
    } else {
      // If scopeCompletionStatus doesn't exist, reset productionData
      setProductionData({});
    }
  };

  const createScopeForLocation = async (
    locationId?: string,
    year?: string,
    scopeCategory?: string,
    payload?: ScopeData
  ) => {
    try {
      setLoading(true);
      const deiredPayload = serialize(ScopeData, payload) as ScopeData[];
      await axiosInstance.put(
        generatePath(ApiRoutes.CREATE_SCOPE, {
          location_id: locationId,
          year,
          scope_category: scopeCategory,
        }),
        { energy_and_fuel_source: deiredPayload }
      );
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgMetaData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(ApiRoutes.META_SIZE_ORG, {
        params: {
          metadata_type: 'organizations',
        },
      });
      const desiredMetaData = deserialize(
        OrganizationMetaData,
        response.data.data.metadata as OrganizationMetaData[]
      );
      setOrgListMeta(desiredMetaData);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const downloadScopeTemplate = async (
    locationId: string,
    year: number,
    scopeCategory: string,
    category?: string
  ) => {
    try {
      setLoading(true);
      const url =
        scopeCategory === scope3
          ? generatePath(ApiRoutes.SCOPE_3_CATEGORY_TEMPLATE, {
            locationId,
            year,
            category,
          })
          : generatePath(ApiRoutes.SCOPE_TEMPLATE, {
            locationId,
            year,
            scopeCategory,
          });
      const response = await axiosInstance.get(url, {
        headers: {
          Accept: XLSX_TYPE,
        },
        responseType: BLOB_TYPE,
      });
      handleBlobDownload(response);
      return true;
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const createScopeThreeForLocation = async (
    locationId?: string,
    year?: string,
    scopeCategory?: string,
    payload?: ScopeThreeData
  ) => {
    try {
      setLoading(true);
      const deiredPayload = serialize(ScopeThreeData, payload) as Record<
        string,
        unknown
      >;
      delete deiredPayload.waste_diversion_rate;
      const response = await axiosInstance.put(
        generatePath(ApiRoutes.SCOPE_CATEGORY, {
          locationId,
          year,
          category: scopeCategory,
        }),
        { waste_treatment: deiredPayload }
      );
      Notification({
        message: response?.data?.message ?? dataUpdatedMessage,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
      return true;
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const uploadScopeTemplate = async (
    locationId: string,
    year: number,
    scopeCategory: string,
    file: RcFile,
    category?: string
  ) => {
    setLoading(true);
    const addedUrl =
      scopeCategory === scope3
        ? generatePath(ApiRoutes.SCOPE_3_CATEGORY_TEMPLATE, {
          locationId,
          year,
          category,
        })
        : generatePath(ApiRoutes.SCOPE_TEMPLATE, {
          locationId,
          year,
          scopeCategory,
        });
    const url = new URL(process.env.REACT_APP_API_BASE_URL + addedUrl);
    const token = localStorage.getItem('user');
    const formData = new FormData();
    const { startMonth } = getMonthName(
      organizationData?.company?.reportingMonth ?? 1
    );
    const reportingPeriod = `${String(startMonth)} ${year}`;
    formData.append(FILE, file);
    const queryParams = {
      reporting_period: reportingPeriod,
    };
    const fullUrl = new URL(url);
    (Object.keys(queryParams) as Array<keyof typeof queryParams>).forEach(key =>
      fullUrl.searchParams.append(key, queryParams[key])
    );
    fetch(fullUrl, {
      method: POST,
      headers: {
        Authorization: String(token),
      },
      body: formData,
    })
      .then(async response => {
        if (response.ok) {
          const data = await response.json();
          Notification({
            message: data?.message,
            description: '',
            type: NotificationTypes.SUCCESS,
          });
          setErrorFile(undefined);
          setIsSuccessfulUpload(true);
          return true;
        } else {
          const errorType = response.headers.get(CONTENT_TYPE);
          if (errorType?.includes(JSON_RESPONSE)) {
            const error = await response.json();
            Notification({
              message: error?.error?.message ?? INVALID_FILE_FORMAT,
              description: '',
              type: NotificationTypes.ERROR,
            });
            setIsSuccessfulUpload(false);
            return;
          } else {
            const blob = await response.blob();
            setErrorFile(blob);
          }
        }
      })
      .catch()
      .finally(() => setLoading(false));
  };

  const fetchWasteTypeMetaData = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(ApiRoutes.META_SIZE_ORG, {
        params: {
          metadata_type: wasteTypesKey,
        },
      });
      const desiredMetaData = deserialize(
        OrganizationMetaData,
        response.data.data.metadata as OrganizationMetaData[]
      );
      setwasteTypeListMeta(desiredMetaData);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchCategory5 = async (params: {
    locationId: string;
    year: number;
  }) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        generatePath(ApiRoutes.SCOPE_CATEGORY, {
          ...params,
          category: category5,
        })
      );
      const scopeThreeData = deserialize(
        ScopeThreeData,
        response.data?.waste_treatment
      );
      setScopeThreeData(scopeThreeData as ScopeThreeData);
      return scopeThreeData;
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchCategory6 = async (params: {
    locationId: string;
    year: number;
  }) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        generatePath(ApiRoutes.SCOPE_CATEGORY, {
          ...params,
          category: category6,
        })
      );
      const businessTravelData = deserialize(
        BusinessTravelData,
        response.data
      ) as BusinessTravelData;
      setBusinessTravelData(businessTravelData);
      return businessTravelData;
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const createCategory6ForLocation = async (
    locationId?: string,
    year?: string,
    scopeCategory?: string,
    payload?: BusinessTravelData
  ) => {
    try {
      setLoading(true);

      // Build the API payload with snake_case keys (matching API specification)
      // Manually transform to avoid serialization issues with plain objects
      const apiPayload = {
        distance_based_travel: (payload?.distanceBasedTravel || []).map(item => {
          const result: Record<string, unknown> = {
            travel_mode: item.travelMode || null,
            origin: item.origin || null,
            destination: item.destination || null,
            round_trip: item.roundTrip ?? false,
            number_of_passengers: item.numberOfPassengers || null,
            car_size: item.travelMode === 'car' ? (item.carSize || null) : null,
            car_type: item.travelMode === 'car' ? (item.carType || null) : null,
            flight_class: item.flightClass || null,
          };
          // Only include id if it's a valid number (for updates)
          if (item.id != null && typeof item.id === 'number' && item.id > 0) {
            result.id = item.id;
          }
          return result;
        }),
        spend_based_travel: (payload?.spendBasedTravel || []).map(item => {
          const result: Record<string, unknown> = {
            spend_type: item.spendType || null,
            money: item.money || null,
            money_unit: item.moneyUnit || null,
            spend_location: item.spendLocation || null,
          };
          // Only include id if it's a valid number (for updates)
          if (item.id != null && typeof item.id === 'number' && item.id > 0) {
            result.id = item.id;
          }
          return result;
        }),
        hotel_stays: (payload?.hotelStays || []).map(item => {
          const result: Record<string, unknown> = {
            hotel_nights: item.hotelNights || null,
            location: item.location || null,
          };
          // Only include id if it's a valid number (for updates)
          if (item.id != null && typeof item.id === 'number' && item.id > 0) {
            result.id = item.id;
          }
          return result;
        }),
      };
      // Use same endpoint pattern as Category 4 - flat structure, no wrapper
      const response = await axiosInstance.put(
        generatePath(ApiRoutes.SCOPE_CATEGORY, {
          locationId,
          year,
          category: category6,
        }),
        apiPayload);
      Notification({
        message: response?.data?.message ?? dataUpdatedMessage,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
      return true;
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const updateProductionData = async (
    locationId?: string,
    year?: string,
    payload?: ProductionData
  ) => {
    try {
      setLoading(true);

      // Convert camelCase to snake_case for API request and ensure valid number or null
      const revenue = payload?.revenue;
      const sanitizedRevenue =
        revenue === null || revenue === undefined || isNaN(revenue)
          ? null
          : revenue;

      // Get the selected currency unit from the payload (user's selection from dropdown)
      // Default to CAD if not provided
      const selectedRevenueUnit = payload?.revenueUnit || 'CAD';

      const requestBody = {
        revenue: sanitizedRevenue,
        revenue_unit: selectedRevenueUnit, // Send whatever currency is selected from dropdown
      };

      await axiosInstance.put(
        generatePath(ApiRoutes.PRODUCTION_DATA, {
          location_id: locationId,
          year,
        }),
        requestBody
      );
      // Note: State will be updated via createDesiredScopeData when fetchYearForLocation refreshes data (same pattern as scope1/scope2)
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    errorFile,
    isSuccessfulUpload,
    createDesiredScopeData,
    scope1Details,
    scope2Details,
    createScopeForLocation,
    fetchOrgMetaData,
    orgListMeta,
    downloadScopeTemplate,
    uploadScopeTemplate,
    createScopeThreeForLocation,
    fetchWasteTypeMetaData,
    wasteTypeListMeta,
    scopeThreeData,
    fetchCategory5,
    businessTravelData,
    fetchCategory6,
    createCategory6ForLocation,
    productionData,
    updateProductionData,
  };
};

export default ActivityDataEntryService;
